import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ClipboardList, Package, PackageCheck, PackagePlus, Users, Droplets, Search, AlertTriangle, CheckCircle2, XCircle, X, Bug } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Notification } from "@/hooks/useNotifications";
import { useNotificationTranslation } from "@/hooks/useNotificationTranslation";
import { useAuth } from "@/contexts/AuthContext";
import { resolveNotificationRoute } from "./NotificationItem";

interface NotificationToastProps {
  notification: Notification | null;
  onDismiss: () => void;
}

const typeConfig: Record<string, { icon: React.ComponentType<{ className?: string }>, color: string }> = {
  task_assigned: { icon: ClipboardList, color: "text-primary bg-primary/10 border-primary/20" },
  task_due_soon: { icon: AlertTriangle, color: "text-amber-500 bg-amber-500/10 border-amber-500/20" },
  task_completed: { icon: CheckCircle2, color: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20" },
  task_cancelled: { icon: XCircle, color: "text-amber-500 bg-amber-500/10 border-amber-500/20" },
  low_stock: { icon: Package, color: "text-amber-500 bg-amber-500/10 border-amber-500/20" },
  team_member_added: { icon: Users, color: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20" },
  treatment_logged: { icon: Droplets, color: "text-blue-500 bg-blue-500/10 border-blue-500/20" },
  irm_warning: { icon: AlertTriangle, color: "text-orange-500 bg-orange-500/10 border-orange-500/20" },
  product_request: { icon: PackagePlus, color: "text-primary bg-primary/10 border-primary/20" },
  product_request_resolved: { icon: PackageCheck, color: "text-emerald-500 bg-emerald-500/10 border-emerald-500/20" },
  worker_quick_report: { icon: Search, color: "text-orange-500 bg-orange-500/10 border-orange-500/20" },
  pest_focus_new: { icon: Bug, color: "text-orange-500 bg-orange-500/10 border-orange-500/20" },
  pest_focus_worse: { icon: Bug, color: "text-red-500 bg-red-500/10 border-red-500/20" },
};

export function NotificationToast({ notification, onDismiss }: NotificationToastProps) {
  const { t } = useTranslation();
  const { translateNotification } = useNotificationTranslation();
  const navigate = useNavigate();
  const { role } = useAuth();
  /* Tocar el aviso emergente lleva a su sitio, como en la campana (antes solo se
     cerraba; auditoría del perito A21/T04-03). */
  const abrir = () => {
    const ruta = notification ? resolveNotificationRoute(notification, role) : undefined;
    onDismiss();
    if (ruta) navigate(ruta);
  };

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        onDismiss();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [notification, onDismiss]);

  const config = typeConfig[notification?.type || ''] || { 
    icon: AlertTriangle, 
    color: "text-muted-foreground bg-muted border-border" 
  };
  const Icon = config.icon;

  // Get translated type label
  const typeLabel = notification?.type 
    ? t(`notifications.types.${notification.type}`, notification.type)
    : '';

  // Get translated title and message
  const translated = notification ? translateNotification(notification) : { title: '', message: '' };

  return (
    <AnimatePresence>
      {notification && (
        <motion.div
          initial={{ opacity: 0, x: 50, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 200, scale: 0.95 }}
          transition={{ 
            type: "spring", 
            stiffness: 400, 
            damping: 30 
          }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.4}
          onDragEnd={(_e, info) => {
            if (Math.abs(info.offset.x) > 100) {
              onDismiss();
            }
          }}
          className="fixed top-20 right-4 z-50 max-w-sm"
        >
          <div 
            className={cn(
              "bg-card border rounded-xl shadow-lg p-4 cursor-pointer",
              "hover:shadow-xl transition-shadow"
            )}
            onClick={abrir}
          >
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className={cn("p-2 rounded-full flex-shrink-0", config.color)}>
                <Icon className="w-4 h-4" />
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0 pr-6">
                {/* Type label */}
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                  {typeLabel}
                </p>
                
                {/* Title */}
                <p className="text-sm font-semibold text-foreground truncate">
                  {translated.title}
                </p>
                
                {/* Message */}
                <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                  {translated.message}
                </p>
              </div>
              
              {/* Close button */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss();
                }}
                className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            
            {/* Progress bar */}
            <motion.div
              initial={{ width: "100%" }}
              animate={{ width: "0%" }}
              transition={{ duration: 4, ease: "linear" }}
              className="absolute bottom-0 left-0 h-1 bg-primary/30 rounded-b-xl"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
